/**
 * ApplyReceiptCxP.js — Aplicar un pago a proveedor SIN APLICAR.
 *
 * Espejo de ApplyReceipt (CxC): el recibo ya está importado (monto, banco,
 * fecha, método fijos). Aquí se elige el proveedor y se distribuye el saldo
 * sin aplicar entre sus facturas pendientes. El exceso sobre el saldo de una
 * factura deja su CuentaXPagar en negativo = saldo a favor del proveedor
 * (saldo deudor en la cuenta del proveedor, Pasivo).
 */
import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../receipts/styles/addReceipt.module.css";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";
import { usePendingReceiptsCxP } from "../hooks/useReceiptCxP";

const r2  = (n) => Math.round(Number(n) * 100) / 100;
const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ApplyReceiptCxP() {
  const { t } = useTranslation();
  const { reciboId } = useParams();
  const navigate = useNavigate();

  const [recibo, setRecibo]       = useState(null);
  const [loadingR, setLoadingR]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [acumulando, setAcumulando]   = useState(false);
  const [acumularMsg, setAcumularMsg] = useState(null);  // {ok, text}
  const [fechaPago, setFechaPago]     = useState("");
  const submittingRef = useRef(false);

  // Proveedor + facturas
  const [cuentasProveedor, setCuentasProveedor] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState(0);
  const [facturasCxP, setFacturasCxP] = useState([]);
  const [loadingFac, setLoadingFac]   = useState(false);
  const [abonos, setAbonos]   = useState({});       // {cxpId: string}
  const [checked, setChecked] = useState(new Set()); // Set<cxpId>
  const [tratarDif, setTratarDif] = useState(false);

  // Cola de pendientes
  const [pendingKey, setPendingKey] = useState(0);
  const { pending } = usePendingReceiptsCxP(pendingKey);

  // ── Cargar recibo ─────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingR(true);
    setRecibo(null);
    submittingRef.current = false;
    setSubmitting(false);
    apiFetch(`/api/finance/reciboCxP/${reciboId}/`)
      .then((r) => r.json())
      .then((data) => {
        setRecibo(data);
        setFechaPago(data.fecha_pago ?? "");
        setLoadingR(false);
      })
      .catch(() => setLoadingR(false));
  }, [reciboId]);

  // ── Cargar proveedores ────────────────────────────────────────────────
  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales&page_size=500")
      .then((r) => r.json())
      .then((d) => setCuentasProveedor(Array.isArray(d) ? d : (d.results ?? [])))
      .catch(() => {});
  }, []);

  const reciboMoneda = (recibo?.moneda_pago || recibo?.cuenta_bancaria?.moneda || "CRC").toUpperCase();
  const reciboTC     = Number(recibo?.tipo_cambio || 1) || 1;

  // Saldo sin aplicar = monto − suma de detalles ya aplicados
  const saldoSinAplicar = useMemo(() => {
    if (!recibo) return 0;
    const aplicado = (recibo.detalles ?? []).reduce((s, d) => s + Number(d.monto || 0), 0);
    return r2(Number(recibo.monto || 0) - aplicado);
  }, [recibo]);

  // ── Cargar facturas del proveedor ─────────────────────────────────────
  useEffect(() => {
    if (!selectedProveedor) { setFacturasCxP([]); setChecked(new Set()); setAbonos({}); return; }
    setLoadingFac(true);
    apiFetch("/api/finance/cuentaXPagar/?page_size=9999")
      .then((r) => r.json())
      .then((d) => {
        const all = d.results ?? d ?? [];
        setFacturasCxP(all.filter(
          (cxp) => cxp.factura_cxp?.cuenta_proveedor?.id === Number(selectedProveedor) && Number(cxp.saldo) > 0
        ));
        setChecked(new Set());
        setAbonos({});
      })
      .finally(() => setLoadingFac(false));
  }, [selectedProveedor]);

  // Costo en la moneda del recibo para cubrir el saldo de una factura
  function costInRecibo(f) {
    const facMon = (f.moneda || "CRC").toUpperCase();
    if (facMon === reciboMoneda) return Number(f.saldo);
    if (reciboMoneda === "CRC")  return Number(f.saldo_colones || 0);   // factura USD
    return Number(f.saldo) / reciboTC;                                  // recibo USD, factura CRC
  }

  const sumAbonos = useMemo(
    () => r2([...checked].reduce((s, id) => s + Number(abonos[id] || 0), 0)),
    [checked, abonos]
  );
  const restante = r2(saldoSinAplicar - sumAbonos);

  function toggleFactura(f) {
    if (checked.has(f.id)) {
      setChecked((prev) => { const n = new Set(prev); n.delete(f.id); return n; });
      setAbonos((a) => { const x = { ...a }; delete x[f.id]; return x; });
    } else {
      const remaining = r2(saldoSinAplicar - sumAbonos);
      const ab = r2(Math.min(costInRecibo(f), Math.max(0, remaining)));
      setChecked((prev) => { const n = new Set(prev); n.add(f.id); return n; });
      setAbonos((a) => ({ ...a, [f.id]: String(ab > 0 ? ab : costInRecibo(f)) }));
    }
  }

  function distribute() {
    let remaining = saldoSinAplicar;
    const nA = {};
    const nC = new Set();
    for (const f of facturasCxP) {
      if (remaining <= 0) break;
      const ab = r2(Math.min(costInRecibo(f), remaining));
      if (ab > 0) { nA[f.id] = String(ab); nC.add(f.id); remaining = r2(remaining - ab); }
    }
    setAbonos(nA);
    setChecked(nC);
  }

  const hayCruzado = useMemo(
    () => facturasCxP.some((f) => checked.has(f.id) && (f.moneda || "CRC").toUpperCase() !== reciboMoneda),
    [facturasCxP, checked, reciboMoneda]
  );

  const siguientes = pending.filter((p) => p.id !== Number(reciboId));

  // ── Guardar aplicación ────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitError("");
    if (!selectedProveedor) { setSubmitError(t("cxp.proveedorReqMsg")); return; }
    if (Math.abs(restante) > 0.01) { setSubmitError(t("recibos.restanteError")); return; }
    const detalles = facturasCxP
      .filter((f) => checked.has(f.id) && Number(abonos[f.id]) > 0)
      .map((f) => ({
        factura_cxp: f.factura_cxp.id,
        monto: r2(Number(abonos[f.id])),
        tratar_como_diferencial: ((f.moneda || "CRC").toUpperCase() !== reciboMoneda) && tratarDif,
      }));
    if (detalles.length === 0) { setSubmitError(t("cxp.seleccionaFacturaCxP")); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${reciboId}/aplicar/`, {
        method: "POST",
        body: JSON.stringify({ detalles, fecha_pago: fechaPago || recibo.fecha_pago }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : t("cxp.errorGuardar"));
      }
      setPendingKey((k) => k + 1);
      const sig = pending.filter((p) => p.id !== Number(reciboId));
      navigate(sig.length > 0 ? `/finanzas/cxp/recibos/${sig[0].id}/aplicar` : "/finanzas/cxp/recibos");
    } catch (err) {
      setSubmitError(err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Acumular como saldo a favor del proveedor ─────────────────────────
  async function handleAcumular() {
    if (acumulando || submittingRef.current) return;
    if (!selectedProveedor) { setAcumularMsg({ ok: false, text: t("cxp.proveedorReqMsg") }); return; }
    setAcumularMsg(null);
    setAcumulando(true);
    submittingRef.current = true;
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${reciboId}/acumular_saldo/`, {
        method: "POST",
        body: JSON.stringify({ proveedor_id: Number(selectedProveedor), fecha_pago: fechaPago || recibo.fecha_pago }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? JSON.stringify(data));
      setAcumularMsg({ ok: true, text: t("cxp.saldoAcumuladoOk", { asiento: data.asiento ?? "—" }) });
      setPendingKey((k) => k + 1);
      setTimeout(() => {
        const sig = pending.filter((p) => p.id !== Number(reciboId));
        navigate(sig.length > 0 ? `/finanzas/cxp/recibos/${sig[0].id}/aplicar` : "/finanzas/cxp/recibos");
      }, 1500);
    } catch (err) {
      setAcumularMsg({ ok: false, text: err.message || t("cxp.errorGuardar") });
      submittingRef.current = false;
    } finally {
      setAcumulando(false);
    }
  }

  if (loadingR) return <p style={{ padding: 20, color: "var(--text-secondary)" }}>{t("common.cargando")}…</p>;
  if (!recibo)  return <p style={{ padding: 20, color: "#f87171" }}>{t("recibos.noEncontrado")}</p>;

  const sinFacturasAbiertas = selectedProveedor && facturasCxP.length === 0;
  const simbolo = reciboMoneda === "USD" ? "$" : "₡";

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} /</span></Link>
        <Link to="/finanzas/cxp"><span className={styles.inactive}> {t("cxp.titulo")} /</span></Link>
        <Link to="/finanzas/cxp/recibos"><span className={styles.inactive}> {t("cxp.recibos")} /</span></Link>
        <span className={styles.active}> {t("recibos.aplicar")} {recibo.numero_recibo}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("recibos.aplicar")} — {recibo.numero_recibo}</h2>

      {/* Banner recibo pendiente */}
      <div style={{ background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.35)",
        borderLeft:"4px solid #f97316", borderRadius:6, padding:"10px 16px", marginBottom:12,
        fontSize:"0.87rem", color:"#fdba74" }}>
        <strong>⚠️ {t("cxp.pagoSinAplicarBanner")}</strong> {t("cxp.pagoSinAplicarDesc")}
        {siguientes.length > 0 && (
          <span style={{ marginLeft:8, color:"#fb923c" }}>
            · {t("recibos.quedanMas", { n: siguientes.length })}
          </span>
        )}
      </div>

      {/* Cola de pendientes */}
      <PendingReceiptsTable
        pendientes={pending}
        currentId={Number(reciboId)}
        pageSize={5}
        applyBasePath="/finanzas/cxp/recibos"
        entityLabel={t("cxp.proveedor")}
        title={t("cxp.colaPendientes", { n: pending.length })}
      />

      {submitError && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"10px 16px", marginBottom:12,
          color:"#f87171", fontSize:"0.86rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{submitError}
        </div>
      )}

      <div className={styles["receipt-container"]}>
        {/* ── Izquierda: datos del recibo (read-only) ── */}
        <div className={styles["receipt-details"]}>
          <div className={styles["receipt-details-header"]}>
            <h1><i className={`fa-solid fa-money-bill-transfer ${styles["icon-receipt-details"]}`}/>{t("cxp.detallesPago")}</h1>
          </div>
          <div className={styles["receipt-details-body"]} style={{ marginTop:14 }}>
            {/* Proveedor (editable: el pago sin aplicar no tiene proveedor aún) */}
            <div>
              <label className="form-label">{t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span></label>
              <select className="form-select" value={selectedProveedor || ""}
                onChange={(e) => setSelectedProveedor(Number(e.target.value) || 0)}>
                <option value="">{t("cxp.seleccionaProveedor")}</option>
                {cuentasProveedor.map((c) => (
                  <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                ))}
              </select>
            </div>

            {/* Monto sin aplicar (read-only) */}
            <div className="row" style={{ marginTop:12 }}>
              <div className="form-group">
                <label className="form-label">
                  {t("recibos.sinAplicar")}{" "}
                  <span style={{ color: reciboMoneda === "USD" ? "#60a5fa" : "#4ade80", fontWeight:700, fontSize:"0.82rem" }}>
                    ({reciboMoneda === "USD" ? "$ USD" : "₡ CRC"})
                  </span>
                </label>
                <input className="form-control" type="text" readOnly
                  value={`${simbolo}${fmt(saldoSinAplicar)}`}
                  style={{ background:"rgba(255,255,255,0.04)", color:"#f97316", fontWeight:600 }}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                  {t("recibos.montoOriginal")}: {simbolo}{fmt(recibo.monto)}
                </div>
              </div>
            </div>

            {/* Fecha + Método (read-only): el pago ya está registrado, solo
                se elige el proveedor para distribuir el saldo */}
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label className="form-label">{t("cxp.fechaPago")}</label>
                  <input className="form-control" type="date" value={fechaPago} readOnly
                    style={{ background:"rgba(255,255,255,0.04)" }}/>
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label className="form-label">{t("cxp.medioPago")}</label>
                  <input className="form-control" type="text" readOnly
                    value={t(`recibos.${(recibo.metodo_pago || "").toLowerCase()}`) || recibo.metodo_pago}
                    style={{ background:"rgba(255,255,255,0.04)" }}/>
                </div>
              </div>
            </div>

            {/* Banco + documento (read-only) */}
            <div className="row">
              <div className="form-group">
                <label className="form-label">{t("cxp.cuentaBancaria")}</label>
                <input className="form-control" type="text" readOnly
                  value={recibo.cuenta_bancaria ? `${recibo.cuenta_bancaria.banco} — ${recibo.cuenta_bancaria.numero_cuenta}` : "—"}
                  style={{ background:"rgba(255,255,255,0.04)" }}/>
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <label className="form-label">{t("cxp.referenciaBancaria")}</label>
                <input className="form-control" type="text" readOnly
                  value={recibo.documento || "—"} style={{ background:"rgba(255,255,255,0.04)" }}/>
              </div>
            </div>

            {/* Diferencial si hay pago cruzado */}
            {hayCruzado && (
              <div style={{ background:"rgba(251,146,60,0.08)", border:"1px solid rgba(251,146,60,0.3)",
                borderRadius:7, padding:"10px 14px", fontSize:"0.8rem" }}>
                <div style={{ color:"#fb923c", fontWeight:600, marginBottom:6 }}>
                  <i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight:6 }}/>{t("cxp.pagoCruzado")}
                </div>
                <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", color:"var(--text-secondary)" }}>
                  <input type="checkbox" checked={tratarDif} onChange={(e) => setTratarDif(e.target.checked)}
                    style={{ accentColor:"#f97316", width:14, height:14 }}/>
                  <span style={{ fontSize:"0.78rem" }}>{t("cxp.tratarDiferencial")}</span>
                </label>
              </div>
            )}

            {/* Info asiento */}
            <div style={{ padding:"10px 14px", background:"rgba(96,165,250,0.06)",
              border:"1px solid rgba(96,165,250,0.2)", borderRadius:6, fontSize:"0.8rem", color:"#93c5fd" }}>
              <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/>
              {t("cxp.asientoInfoPrefix")} <strong>{t("asientos.debe").toUpperCase()}</strong> {t("cxp.proveedorDefault")}
              {" · "}<strong>{t("asientos.haber").toUpperCase()}</strong> {t("cxp.bancoLabel")}
            </div>
          </div>
        </div>

        {/* ── Derecha: facturas del proveedor ── */}
        <div className={styles["cxc-details"]}>
          <div className={styles["cxc-details-header"]}>
            <h1><i className={`fa-solid fa-file-invoice ${styles["icon-cxc-details"]}`}/>{t("cxp.facturasPendientes")}</h1>
          </div>
          <div className={styles["receipt-details-body"]}>
            {!selectedProveedor ? (
              <div style={{ padding:"40px 16px", textAlign:"center", color:"var(--text-muted)" }}>
                <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block", marginBottom:12 }}/>
                {t("cxp.sinProveedor")}
              </div>
            ) : loadingFac ? (
              <div style={{ padding:40, textAlign:"center" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ color:"#f97316", fontSize:"1.3rem" }}/>
              </div>
            ) : facturasCxP.length === 0 ? (
              <div style={{ padding:32, textAlign:"center", color:"var(--text-muted)", fontSize:"0.87rem" }}>
                <i className="fa-solid fa-check-circle" style={{ color:"#4ade80", fontSize:"1.4rem", display:"block", marginBottom:8 }}/>
                {t("cxp.sinFacturasCxP")}
                <div style={{ fontSize:"0.8rem", marginTop:8 }}>{t("cxp.acumularHint")}</div>
              </div>
            ) : (
              <>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <button type="button" onClick={distribute}
                    style={{ padding:"6px 12px", background:"rgba(249,115,22,0.12)", border:"1px solid rgba(249,115,22,0.4)",
                      borderRadius:6, color:"#f97316", fontWeight:600, fontSize:"0.8rem", cursor:"pointer" }}>
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight:6 }}/>{t("recibos.distribuir")}
                  </button>
                  <span style={{ fontSize:"0.82rem", color: Math.abs(restante) < 0.01 ? "#4ade80" : "#f87171", fontWeight:600 }}>
                    {t("recibos.restante")}: {simbolo}{fmt(restante)}
                  </span>
                </div>

                <div style={{ width:"100%", overflowX:"auto", border:"1px solid var(--border-card)", borderRadius:8 }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", color:"var(--text-primary)" }}>
                    <thead>
                      <tr style={{ background:"var(--bg-card)", borderBottom:"1px solid var(--border-card)" }}>
                        <th style={{ width:34, padding:"10px 8px" }}></th>
                        <th style={{ padding:"10px 10px", textAlign:"left", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("cxp.numFactura")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"center", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("common.moneda")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"right", color:"var(--text-secondary)", fontSize:"0.78rem" }}>{t("cxp.saldo")}</th>
                        <th style={{ padding:"10px 10px", textAlign:"right", color:"var(--text-secondary)", fontSize:"0.78rem", width:"30%" }}>{t("recibos.abono")} ({simbolo})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facturasCxP.map((cxp) => {
                        const fac = cxp.factura_cxp;
                        const sel = checked.has(cxp.id);
                        const facMon = (cxp.moneda || "CRC").toUpperCase();
                        return (
                          <tr key={cxp.id} style={{ background: sel ? "rgba(249,115,22,0.08)" : "transparent",
                            borderBottom:"1px solid var(--border-card)" }}>
                            <td style={{ textAlign:"center", padding:"8px" }}>
                              <input type="checkbox" checked={sel} onChange={() => toggleFactura(cxp)}
                                style={{ accentColor:"#f97316" }}/>
                            </td>
                            <td style={{ padding:"8px 10px", fontFamily:"monospace", color:"#f97316", fontWeight:600, fontSize:"0.82rem", cursor:"pointer" }}
                              onClick={() => toggleFactura(cxp)}>
                              {fac?.numero_factura ?? "—"}
                            </td>
                            <td style={{ padding:"8px 10px", textAlign:"center" }}>
                              <span style={{ padding:"2px 7px", borderRadius:4, fontSize:"0.7rem", fontWeight:700,
                                background: facMon === "USD" ? "rgba(96,165,250,0.15)" : "rgba(74,222,128,0.12)",
                                color: facMon === "USD" ? "#60a5fa" : "#4ade80" }}>{facMon}</span>
                            </td>
                            <td style={{ padding:"8px 10px", textAlign:"right", fontFamily:"monospace", color:"#facc15", fontWeight:600 }}>
                              {facMon === "USD" ? `$${fmt(cxp.saldo)}` : `₡${fmt(cxp.saldo)}`}
                            </td>
                            <td style={{ padding:"6px 10px", textAlign:"right" }}>
                              <input type="number" min="0" step="0.01" disabled={!sel}
                                value={sel ? (abonos[cxp.id] ?? "") : ""}
                                onChange={(e) => setAbonos((a) => ({ ...a, [cxp.id]: e.target.value }))}
                                style={{ width:"100%", textAlign:"right", padding:"5px 8px", borderRadius:5,
                                  border:"1px solid var(--border-card)", background: sel ? "var(--bg-input,#1c1c1c)" : "transparent",
                                  color:"var(--text-primary)" }}/>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Banner acumular */}
        {acumularMsg && (
          <div style={{ gridColumn:"1 / -1", margin:"0 0 8px", padding:"10px 16px", borderRadius:6,
            background: acumularMsg.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
            border: `1px solid ${acumularMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
            borderLeft: `4px solid ${acumularMsg.ok ? "#4ade80" : "#f87171"}`,
            color: acumularMsg.ok ? "#4ade80" : "#f87171", fontSize:"0.87rem",
            display:"flex", alignItems:"center", gap:8 }}>
            <i className={`fa-solid ${acumularMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}/>{acumularMsg.text}
          </div>
        )}

        {/* ── Footer ── */}
        <div className={styles["receipt-footer"]}>
          <div className={styles.actions}>
            <Link to="/finanzas/cxp/recibos" className={`${styles.btn} ${styles["btn-clean-form"]}`} style={{ textDecoration:"none" }}>
              <i className="fa-solid fa-arrow-left"/> {t("recibos.volverSinGuardar")}
            </Link>

            {sinFacturasAbiertas && (
              <button type="button" onClick={handleAcumular} disabled={acumulando || submitting}
                style={{ padding:"9px 18px", background:"rgba(167,139,250,0.15)", border:"1px solid rgba(167,139,250,0.45)",
                  borderRadius:6, color:"#a78bfa", fontWeight:600, fontSize:14, cursor: acumulando ? "not-allowed" : "pointer",
                  display:"flex", alignItems:"center", gap:7, opacity: acumulando ? 0.7 : 1 }}>
                {acumulando
                  ? <><span className="spinner-border spinner-border-sm" role="status"/>&nbsp;{t("cxp.acumulando")}</>
                  : <><i className="fa-solid fa-piggy-bank"/> {t("cxp.acumularSaldoFavor")}</>}
              </button>
            )}

            <button className={`${styles.btn} ${styles["btn-save"]}`} onClick={handleSubmit} disabled={submitting || acumulando}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm" role="status"/>&nbsp;{t("cxp.guardando")}</>
                : <><i className="fa-solid fa-save"/> {siguientes.length > 0
                    ? t("recibos.guardarYSiguiente", { n: siguientes.length })
                    : t("recibos.guardarAplicacion")}</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
